import type { FilterValue } from 'antd/lib/table/interface';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import useBaseFilters from '@/hooks/useBaseFilters';
import { useAppSelector } from '@/store';

import { statusOptions } from '../data/statusOptions.data';
import useFilterParamValues from './useFilterParamValues';

interface IFilters {
  autoType: string;
  banks: string[];
  dealers: string[];
  models: string[];
  onlineRequest: string;
  period: string[];
  statuses: string[];
  user: string;
}

const TYPES_ORDER = [
  'Новое Авто',
  'Коммерческое',
  'С Первичной Регистрацией',
  'Б\\У авто', // На тесте все еще 'Б\\У авто'
  'Б\\У'
];

const useFilters = () => {
  const { dicts } = useAppSelector((state) => state.dict);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = new URLSearchParams(searchParams);

  const {
    paginationData,
    selectedUser,
    searchValue,
    debouncedSearchValue,
    handleSearch,
    handleUserChange
  } = useBaseFilters();
  const filterParamValues = useFilterParamValues();
  const {
    activeOnlineRequest,
    activeParamBanks,
    activeParamDealers,
    activeParamModels,
    activeParamPeriod
  } = filterParamValues;

  const autoTypes = useMemo(() => {
    return (
      dicts?.Auto_Types?.content
        .map((item) => ({
          label: item.name,
          value: item.uuid
        }))
        .sort(
          (a, b) => TYPES_ORDER.indexOf(a.label) - TYPES_ORDER.indexOf(b.label)
        ) || []
    );
  }, [dicts]);

  const activeAutoType = autoTypes.find(
    (item) => item.value === searchParams.get('auto-type')
  );

  const handleApplyFilter = ({
    autoType,
    banks,
    dealers,
    models,
    onlineRequest,
    period,
    statuses,
    user
  }: IFilters) => {
    params.set('auto-type', autoType);

    if (dealers.length === 0) params.delete('dealers');
    else params.set('dealers', dealers.join(','));

    if (onlineRequest === 'all') params.delete('online-request');
    else params.set('online-request', onlineRequest);

    if (!period[0] || !period[1]) params.delete('period');
    else params.set('period', period.join(','));

    if (models.length === 0) params.delete('models');
    else params.set('models', models.join(','));

    if (statuses.length === 0) params.delete('statuses');
    else params.set('statuses', statuses.join(','));

    if (banks.length === 0) params.delete('banks');
    else params.set('banks', banks.join(','));

    params.set('page', '1');
    params.set('per-page', String(paginationData.itemsPerPage || 10));

    handleUserChange(user, params, true);
  };

  const handleFiltersChange = (filters: Record<string, FilterValue | null>) => {
    if (activeAutoType) {
      const selectedStatuses = statusOptions
        .filter((item) => (filters.status || []).includes(item.value))
        .map((item) => item.value);

      handleApplyFilter({
        autoType: activeAutoType.value,
        banks: activeParamBanks.map((item) => item.value),
        dealers: activeParamDealers.map((item) => item.value),
        models: activeParamModels.map((item) => item.value),
        onlineRequest: activeOnlineRequest,
        period: activeParamPeriod,
        statuses: selectedStatuses,
        user: selectedUser || '-1'
      });
    }
  };

  useEffect(() => {
    if (!activeAutoType && autoTypes.length > 0) {
      params.set('auto-type', autoTypes[0].value);
      setSearchParams(params, { replace: true });
    }
  }, [activeAutoType, autoTypes]);

  return {
    activeAutoType,
    autoTypes,
    debouncedSearchValue,
    filterParamValues,
    handleApplyFilter,
    handleFiltersChange,
    handleSearch,
    handleUserChange,
    paginationData,
    searchValue,
    selectedUser
  };
};

export default useFilters;
